import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import useSWR from 'swr';
import axios from 'axios';
import { apiHost } from '../utils/apiHost';
import { konfirmasi } from '../utils/dialog';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

const fetcher = (url) => axiosInstance.get(url).then((res) => res.data).catch((err) => {
  if (err.response?.status === 404) return { status: true, data: null };
  throw err;
});

const defaultTestimoni = () => ({ nama: '', peran: '', kutipan: '', rating: 5 });
const defaultFaq = () => ({ pertanyaan: '', jawaban: '' });

// Pengaturan halaman menu (landing page) satu cabang, tampil di subdomain cabang tsb.
// Dipakai admin cabang (untuk cabangnya sendiri) dan superadmin (untuk cabang manapun via cabangUuid prop).
export const BranchLandingSettings = ({ cabangUuid }) => {
  const { data, isLoading, mutate } = useSWR(
    cabangUuid ? `/getkonfigurasicabang?cabangUuid=${cabangUuid}` : null,
    fetcher
  );

  const [form, setForm] = useState({ landingHeroBadge: '', landingHeroTagline: '', landingAbout: '', jamOperasional: '', landingAccentColor: '#2563eb', landingTheme: 'light' });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [testimonis, setTestimonis] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.data) {
      setForm({
        landingHeroBadge: data.data.landingHeroBadge || '',
        landingHeroTagline: data.data.landingHeroTagline || '',
        landingAbout: data.data.landingAbout || '',
        jamOperasional: data.data.jamOperasional || '',
        landingAccentColor: data.data.landingAccentColor || '#2563eb',
        landingTheme: data.data.landingTheme || 'light',
      });
      setTestimonis(Array.isArray(data.data.landingTestimonis) ? data.data.landingTestimonis : []);
      setFaqs(Array.isArray(data.data.landingFaqs) ? data.data.landingFaqs : []);
    }
  }, [data]);

  const currentCoverUrl = data?.data?.landingCoverImage ? `${getApiBaseUrl()}/uploads/${data.data.landingCoverImage}` : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateTestimoni = (idx, changes) => setTestimonis((prev) => prev.map((t, i) => (i === idx ? { ...t, ...changes } : t)));
  const addTestimoni = () => setTestimonis((prev) => [...prev, defaultTestimoni()]);
  const removeTestimoni = (idx) => setTestimonis((prev) => prev.filter((_, i) => i !== idx));

  const updateFaq = (idx, changes) => setFaqs((prev) => prev.map((f, i) => (i === idx ? { ...f, ...changes } : f)));
  const addFaq = () => setFaqs((prev) => [...prev, defaultFaq()]);
  const removeFaq = (idx) => setFaqs((prev) => prev.filter((_, i) => i !== idx));

  const handleRemoveCover = async () => {
    if (!await konfirmasi('Hapus foto cover halaman menu ini?')) return;
    setSaving(true);
    setFeedback(null);
    try {
      await axiosInstance.put('/updatekonfigurasicabang', { cabangUuid, removeLandingCoverImage: true });
      mutate();
      setFeedback({ type: 'success', msg: 'Foto cover dihapus.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menghapus.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append('cabangUuid', cabangUuid);
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      formData.append('landingTestimonis', JSON.stringify(testimonis));
      formData.append('landingFaqs', JSON.stringify(faqs));
      if (coverFile) formData.append('landingCoverImage', coverFile);
      await axiosInstance.put('/updatekonfigurasicabang', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCoverFile(null);
      setCoverPreview(null);
      mutate();
      setFeedback({ type: 'success', msg: 'Halaman menu cabang berhasil disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  if (!cabangUuid) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Halaman Menu Cabang"
        subheader="Profil lengkap cabang ini yang tampil di subdomain menu (mis. namacabang.domainanda.com): hero, tentang, testimoni, dan FAQ. Menu unggulan otomatis diambil dari data produk cabang."
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Foto Cover</Typography>
                <Box sx={{
                  width: '100%', maxWidth: 400, height: 160, border: '1px dashed #ccc', borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa', mb: 1,
                }}>
                  {(coverPreview || currentCoverUrl) ? (
                    <img src={coverPreview || currentCoverUrl} alt="Cover" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', width: '100%', height: '100%' }} />
                  ) : (
                    <ImageIcon sx={{ color: '#ccc', fontSize: 40 }} />
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" component="label" size="small" disabled={saving}>
                    Pilih Gambar
                    <input type="file" accept="image/*" hidden onChange={(e) => {
                      const f = e.target.files[0];
                      setCoverFile(f || null);
                      setCoverPreview(f ? URL.createObjectURL(f) : null);
                    }} />
                  </Button>
                  {currentCoverUrl && !coverFile && (
                    <Button variant="outlined" color="error" size="small" onClick={handleRemoveCover} disabled={saving}>
                      Hapus
                    </Button>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Warna Aksen</Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    component="input"
                    type="color"
                    value={form.landingAccentColor}
                    onChange={(e) => setForm((prev) => ({ ...prev, landingAccentColor: e.target.value }))}
                    disabled={saving}
                    sx={{ width: 48, height: 40, border: '1px solid #ccc', borderRadius: 1, p: 0.3, cursor: 'pointer', bgcolor: 'transparent' }}
                  />
                  <TextField
                    size="small"
                    value={form.landingAccentColor}
                    onChange={(e) => setForm((prev) => ({ ...prev, landingAccentColor: e.target.value }))}
                    disabled={saving}
                    sx={{ width: 120 }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Tema Halaman</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={form.landingTheme}
                  onChange={(e, val) => val && setForm((prev) => ({ ...prev, landingTheme: val }))}
                  disabled={saving}
                >
                  <ToggleButton value="light">Terang</ToggleButton>
                  <ToggleButton value="dark">Gelap</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Badge Hero" placeholder="Selamat Datang di Kopi Sawah" name="landingHeroBadge" value={form.landingHeroBadge} onChange={handleChange} disabled={saving} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Judul / Tagline" placeholder="Kosongkan untuk pakai nama cabang" name="landingHeroTagline" value={form.landingHeroTagline} onChange={handleChange} disabled={saving} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label="Tentang Kami" placeholder="Ceritakan singkat tentang tempat ini..." name="landingAbout" value={form.landingAbout} onChange={handleChange} disabled={saving} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Jam Operasional" placeholder="Setiap hari, 08.00 - 20.00" name="jamOperasional" value={form.jamOperasional} onChange={handleChange} disabled={saving} />
              </Grid>
            </Grid>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Testimoni</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Kosongkan semua (hapus semua kartu) untuk menyembunyikan bagian testimoni di halaman menu.</Typography>
              <Stack spacing={2}>
                {testimonis.map((t, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={2} label="Kutipan" value={t.kutipan} onChange={(e) => updateTestimoni(idx, { kutipan: e.target.value })} disabled={saving} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Nama" value={t.nama} onChange={(e) => updateTestimoni(idx, { nama: e.target.value })} disabled={saving} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Peran" placeholder="Pelanggan Setia" value={t.peran} onChange={(e) => updateTestimoni(idx, { peran: e.target.value })} disabled={saving} />
                      </Grid>
                      <Grid item xs={8} sm={3}>
                        <TextField fullWidth type="number" label="Rating (1-5)" value={t.rating} onChange={(e) => updateTestimoni(idx, { rating: Math.min(5, Math.max(1, Number(e.target.value) || 1)) })} disabled={saving} InputProps={{ inputProps: { min: 1, max: 5 } }} />
                      </Grid>
                      <Grid item xs={4} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <IconButton color="error" onClick={() => removeTestimoni(idx)} disabled={saving}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button startIcon={<AddIcon />} onClick={addTestimoni} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                  Tambah Testimoni
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>FAQ (Pertanyaan Umum)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Kosongkan semua (hapus semua kartu) untuk menyembunyikan bagian FAQ di halaman menu.</Typography>
              <Stack spacing={2}>
                {faqs.map((f, idx) => (
                  <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={5}>
                        <TextField fullWidth label="Pertanyaan" value={f.pertanyaan} onChange={(e) => updateFaq(idx, { pertanyaan: e.target.value })} disabled={saving} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth multiline minRows={1} label="Jawaban" value={f.jawaban} onChange={(e) => updateFaq(idx, { jawaban: e.target.value })} disabled={saving} />
                      </Grid>
                      <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <IconButton color="error" onClick={() => removeFaq(idx)} disabled={saving}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button startIcon={<AddIcon />} onClick={addFaq} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                  Tambah FAQ
                </Button>
              </Stack>
            </Box>

            <Box>
              <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
                Simpan Halaman Menu
              </Button>
            </Box>
            {feedback && <Alert severity={feedback.type}>{feedback.msg}</Alert>}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default BranchLandingSettings;
